import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { registerSessionCleanup } from '../auth/sessionCleanup';

type ClientOption = { supabase?: SupabaseClient };

export type UserPresenceStatus = 'online' | 'idle' | 'out' | 'offline';

export type UserPresence = {
  profile_id: string;
  status: UserPresenceStatus;
  last_seen_at: string;
  last_activity_at: string;
  updated_at: string;
};

export const PRESENCE_IDLE_AFTER_MS = 2 * 60 * 1000;
export const PRESENCE_OUT_AFTER_MS = 10 * 60 * 1000;
export const PRESENCE_SESSION_STALE_MS = 15 * 60 * 1000;
export const PRESENCE_KEEPALIVE_MS = 4 * 60 * 1000;
export const PRESENCE_EVALUATION_INTERVAL_MS = 60 * 1000;
export const PRESENCE_ACTIVITY_THROTTLE_MS = 15 * 1000;
export const PRESENCE_POLL_INTERVAL_MS = 60 * 1000;

const PRESENCE_SESSION_STORAGE_KEY = 'ita.app.presenceSessionId';
const PRESENCE_LOOKUP_CHUNK_SIZE = 100;
let presenceSessionIdCache: string | null = null;

export function resetPresenceSessionIdentity(): void {
  presenceSessionIdCache = null;
}

registerSessionCleanup(resetPresenceSessionIdentity);

const PRESENCE_STATUS_LABELS: Record<UserPresenceStatus, string> = {
  online: 'Online',
  idle: 'Idle',
  out: 'Out',
  offline: 'Offline',
};

const PRESENCE_STATUS_COLORS: Record<UserPresenceStatus, string> = {
  online: '#22c55e',
  idle: '#eab308',
  out: '#dc2626',
  offline: '#94a3b8',
};

function createPresenceSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `presence-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function getPresenceSessionId(): string {
  if (presenceSessionIdCache) return presenceSessionIdCache;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const existing = window.sessionStorage.getItem(PRESENCE_SESSION_STORAGE_KEY);
      if (existing) {
        presenceSessionIdCache = existing;
        return existing;
      }
      const next = createPresenceSessionId();
      window.sessionStorage.setItem(PRESENCE_SESSION_STORAGE_KEY, next);
      presenceSessionIdCache = next;
      return next;
    }
  } catch {
    // Storage can be blocked in private windows. In-memory still gives this tab
    // a stable enough key until reload.
  }
  presenceSessionIdCache = createPresenceSessionId();
  return presenceSessionIdCache;
}

export function derivePresenceStatus(inactiveForMs: number): Exclude<UserPresenceStatus, 'offline'> {
  if (inactiveForMs < PRESENCE_IDLE_AFTER_MS) return 'online';
  if (inactiveForMs < PRESENCE_OUT_AFTER_MS) return 'idle';
  return 'out';
}

function isPresenceStatus(value: unknown): value is UserPresenceStatus {
  return value === 'online' || value === 'idle' || value === 'out' || value === 'offline';
}

function isPresenceStale(updatedAt: string | null | undefined): boolean {
  if (!updatedAt) return true;
  const ts = Date.parse(updatedAt);
  if (!Number.isFinite(ts)) return true;
  return Date.now() - ts > PRESENCE_SESSION_STALE_MS;
}

export function normalisePresenceRow(row: Partial<UserPresence> & { profile_id: string }): UserPresence {
  const status = isPresenceStatus(row.status) ? row.status : 'offline';
  return {
    profile_id: row.profile_id,
    status: isPresenceStale(row.updated_at) ? 'offline' : status,
    last_seen_at: row.last_seen_at ?? '',
    last_activity_at: row.last_activity_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

export function getPresenceStatusLabel(status: UserPresenceStatus | null | undefined): string {
  return PRESENCE_STATUS_LABELS[status ?? 'offline'] ?? PRESENCE_STATUS_LABELS.offline;
}

export function getPresenceStatusColor(status: UserPresenceStatus | null | undefined): string {
  return PRESENCE_STATUS_COLORS[status ?? 'offline'] ?? PRESENCE_STATUS_COLORS.offline;
}

export async function updateUserPresenceForProfile(
  profileId: string,
  status: UserPresenceStatus,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  if (!profileId) return null;
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error: sessionError } = await supabase.rpc('update_user_presence_session', {
    p_profile_id: profileId,
    p_session_id: getPresenceSessionId(),
    p_status: status,
  });
  if (!sessionError) return null;
  const isMissingSessionRpc = sessionError.code === '42883'
    || sessionError.code === 'PGRST202'
    || String(sessionError.message || '').includes('update_user_presence_session');
  if (!isMissingSessionRpc) {
    return sessionError ?? null;
  }
  const { error } = await supabase.rpc('update_user_presence', {
    p_profile_id: profileId,
    p_status: status,
  });
  return error ?? null;
}

export async function updateUserPresence(
  status: UserPresenceStatus,
  options?: ClientOption & { profileId?: string | null },
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const profileId = options?.profileId
    ?? (await supabase.rpc('current_user_profile_id_safe')).data
    ?? null;
  if (!profileId) return null;
  return updateUserPresenceForProfile(String(profileId), status, { supabase });
}

async function fetchPresenceSnapshot(
  supabase: SupabaseClient,
  trackedProfileIds: string[],
): Promise<{ presences: Record<string, UserPresence>; error: PostgrestError | null }> {
  const presences: Record<string, UserPresence> = {};
  let firstError: PostgrestError | null = null;

  for (let index = 0; index < trackedProfileIds.length; index += PRESENCE_LOOKUP_CHUNK_SIZE) {
    const chunk = trackedProfileIds.slice(index, index + PRESENCE_LOOKUP_CHUNK_SIZE);
    const { data, error } = await supabase
      .from('user_presence')
      .select('profile_id,status,last_seen_at,last_activity_at,updated_at')
      .in('profile_id', chunk);
    if (error) {
      firstError = firstError ?? error;
      continue;
    }
    for (const row of data ?? []) {
      const normalised = normalisePresenceRow(row as UserPresence);
      presences[normalised.profile_id] = normalised;
    }
  }

  for (const id of trackedProfileIds) {
    if (!presences[id]) {
      presences[id] = normalisePresenceRow({
        profile_id: id,
        status: 'offline',
      });
    }
  }

  return { presences, error: firstError };
}

export function subscribeToUserPresence(
  profileIds: string[],
  onChange: (presences: Record<string, UserPresence>) => void,
  options?: ClientOption,
): () => void {
  const supabase = options?.supabase ?? getSupabaseClient();
  const trackedProfileIds = Array.from(new Set(profileIds.filter(Boolean)));
  if (trackedProfileIds.length === 0) {
    onChange({});
    return () => {};
  }

  let cancelled = false;

  const refresh = async (reason: 'initial' | 'poll' | 'visible') => {
    const { presences, error } = await fetchPresenceSnapshot(supabase, trackedProfileIds);
    if (cancelled) return;
    if (error) {
      console.warn(`[presence] Failed to fetch ${reason} presence snapshot:`, error);
    }
    onChange(presences);
  };

  void refresh('initial');

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {
      cancelled = true;
    };
  }

  const pollInterval = window.setInterval(() => {
    if (document.visibilityState === 'hidden') return;
    void refresh('poll');
  }, PRESENCE_POLL_INTERVAL_MS);

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      void refresh('visible');
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);

  return () => {
    cancelled = true;
    document.removeEventListener('visibilitychange', handleVisibility);
    window.clearInterval(pollInterval);
  };
}
